import { getAutoHeightDuration } from '@core/transitions/constants';

/**
 * Normalizes collapsedSize prop to a CSS height string (e.g. "0px", "48px").
 */
export function normalizeCollapsedSize(
  value: string | number | undefined
): string {
  if (value === undefined) return '0px';
  return typeof value === 'number' ? `${value}px` : value;
}

export type GetCollapseDurationOpts = {
  reducedMotion: boolean;
  isAuto: boolean;
  measuredHeight: number;
  enterDuration: number;
  exitDuration: number;
};

/**
 * Returns duration in ms for collapse enter/exit.
 * Handles reduced motion (0), auto (from content height or exit fallback), and fixed enter/exit.
 */
export function getCollapseDuration(
  mode: 'enter' | 'exit',
  opts: GetCollapseDurationOpts
): number {
  if (opts.reducedMotion) return 0;
  if (opts.isAuto) {
    return opts.measuredHeight > 0
      ? getAutoHeightDuration(opts.measuredHeight)
      : opts.exitDuration;
  }
  return mode === 'enter' ? opts.enterDuration : opts.exitDuration;
}

export type GetCollapseStateStylesOpts = {
  collapsedSize: string;
  exitHeightPx: number;
  inProp: boolean;
  isZeroCollapsed: boolean;
};

export type CollapseStateStyles = {
  height: string;
  overflow: 'visible' | 'hidden';
  visibility: 'hidden' | undefined;
};

/**
 * Derives height, overflow, and visibility for the root div from transition state.
 */
export function getCollapseStateStyles(
  state: string,
  opts: GetCollapseStateStylesOpts
): CollapseStateStyles {
  let height: string;
  if (state === 'exited') {
    height = opts.collapsedSize;
  } else if (state === 'entered') {
    height = 'auto';
  } else if (state === 'exiting') {
    height = `${opts.exitHeightPx}px`;
  } else {
    // 'entering': keep collapsedSize so the inline style holds the starting height.
    // handleEntering then reads the wrapper height and animates to it.
    height = opts.collapsedSize;
  }

  const overflow = state === 'entered' ? ('visible' as const) : 'hidden';

  let visibility: 'hidden' | undefined;
  if (state === 'exited' && !opts.inProp && opts.isZeroCollapsed) {
    visibility = 'hidden';
  } else {
    visibility = undefined;
  }

  return { height, overflow, visibility };
}
